// i18n deferred: admin/blog/legal pages are intentionally excluded from Step 2 migration.
import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'sonner';
import i18n from '@/i18n';

import { Input } from '../components/ui/atoms/Input';
import { Label } from '../components/ui/label';
import { Button } from '@/components/ui/atoms/Button';

interface SearchResult {
  id: string;
  email: string | null;
  tenant_id: string | null;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  created_at: string | null;
  has_user_plan: boolean;
  pending_invitations: number;
  job_offers: number;
  candidates_referenced: number;
  payment_transactions: number;
}

interface CleanupResult {
  deleted_user_id: string;
  deleted_email: string | null;
  cancelled_invitations: number;
  deleted_user_plans: number;
  deleted_usage_events: number;
  deleted_job_offers: number;
  deleted_purchase_sessions: number;
  tenant_deleted: boolean;
  auth0_user_deleted: boolean;
}

const apiUrl = import.meta.env.VITE_API_SERVER_URL as string;

/**
 * Inner content — embeddable inside the AdminPage tab grid OR rendered
 * directly as a standalone page. Same component, different shells.
 */
export const AdminUserCleanupSection: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState<SearchResult | null>(null);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [reason, setReason] = useState('');
  const [force, setForce] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const hasBusinessData = (r: SearchResult): boolean =>
    r.job_offers > 0 || r.payment_transactions > 0;

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const param = query.includes('|') ? `sub=${encodeURIComponent(query)}` : `email=${encodeURIComponent(query)}`;
      const resp = await fetch(`${apiUrl}/api/admin/users/search?${param}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(i18n.t('common:toasts.admin.search_failed'));
      setResults(await resp.json());
    } catch (err) {
      const searchFallback = i18n.t('common:toasts.admin.search_failed');
      const message = err instanceof Error ? err.message.trim() : '';
      toast.error(
        message && message !== i18n.t('common:toasts.admin.search_failed', { lng: 'en' })
          ? message
          : searchFallback,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async () => {
    if (!target || submitting) return;
    if (confirmEmail !== target.email) {
      toast.error(i18n.t('common:toasts.admin.confirm_email_mismatch'));
      return;
    }
    if (!reason.trim()) {
      toast.error(i18n.t('common:toasts.admin.reason_required'));
      return;
    }
    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const resp = await fetch(`${apiUrl}/api/admin/users/${encodeURIComponent(target.id)}/cleanup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reason,
          delete_auth0_user: true,
          preserve_invitations: false,
          force,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        if (resp.status === 409) {
          toast.error(
            data.detail?.message ?? i18n.t('common:toasts.admin.cleanup_business_data_conflict'),
            { duration: 6000 },
          );
        } else {
          toast.error(typeof data.detail === 'string' ? data.detail : i18n.t('common:toasts.unexpected_error'));
        }
        return;
      }
      const result = data as CleanupResult;
      toast.success(
        i18n.t('common:toasts.admin.cleanup_success', {
          email: result.deleted_email ?? target.id,
          plans: result.deleted_user_plans,
          invitations: result.cancelled_invitations,
          jobs: result.deleted_job_offers,
          tenant: result.tenant_deleted
            ? i18n.t('common:toasts.admin.cleanup_tenant_deleted')
            : i18n.t('common:toasts.admin.cleanup_tenant_kept'),
          auth0: result.auth0_user_deleted
            ? i18n.t('common:toasts.admin.cleanup_auth0_deleted')
            : i18n.t('common:toasts.admin.cleanup_auth0_kept'),
        }),
        { duration: 7000 },
      );
      setTarget(null);
      setConfirmEmail('');
      setReason('');
      setForce(false);
      setResults((rs) => rs.filter((r) => r.id !== target.id));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <h1 className="text-2xl font-bold text-dark-invirtus mb-2">
        User cleanup
      </h1>
      <p className="text-sm text-dark-invirtus/70 mb-6">
        Wipe a user record end-to-end (DB rows, plans, invitations,
        tenant, Auth0) so the same email can sign up again. Use Force
        to also wipe associated business data.
      </p>

        <form onSubmit={handleSearch} className="flex gap-3 mb-8">
          <Input
            placeholder="Email (partial match) or auth0|sub"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            rounded="lg"
          />
          <Button
            label="Search"
            intent="secondary"
            htmlType="submit"
            disabled={loading || !query.trim()}
            loading={loading}
          />
        </form>

        {results.length === 0 && query.trim().length > 0 && !loading && (
          <p className="text-sm text-dark-invirtus/50">No users found.</p>
        )}

        <div className="space-y-3">
          {results.map((r) => (
            <div
              key={r.id}
              className="bg-white rounded-lg border border-light-lila p-4 flex items-start justify-between gap-4"
            >
              <div className="text-sm">
                <div className="font-semibold text-dark-invirtus">
                  {r.email ?? '(no email)'}
                </div>
                <div className="text-dark-invirtus/60 mt-0.5">
                  {[r.first_name, r.last_name].filter(Boolean).join(' ') || '(no name)'}
                  {r.company ? ` · ${r.company}` : ''}
                </div>
                <div className="text-xs text-dark-invirtus/50 mt-1 font-mono">{r.id}</div>
                <div className="flex flex-wrap gap-3 mt-2 text-xs text-dark-invirtus/70">
                  <span>plan: {r.has_user_plan ? 'yes' : 'no'}</span>
                  <span>tenant: {r.tenant_id ? 'yes' : 'no'}</span>
                  <span>invitations: {r.pending_invitations}</span>
                  <span className={r.job_offers > 0 ? 'font-semibold text-amber-700' : ''}>jobs: {r.job_offers}</span>
                  <span>candidates: {r.candidates_referenced}</span>
                  <span className={r.payment_transactions > 0 ? 'font-semibold text-amber-700' : ''}>payments: {r.payment_transactions}</span>
                </div>
                {hasBusinessData(r) && (
                  <p className="text-xs text-amber-700 mt-2">
                    Has business data — Force toggle will be required to wipe.
                  </p>
                )}
              </div>
              <Button
                label="Reset user"
                type="outlined"
                intent="danger"
                size="sm"
                className="flex-shrink-0"
                onClick={() => {
                  setTarget(r);
                  setConfirmEmail('');
                  setReason('');
                  setForce(false);
                }}
              />
            </div>
          ))}
        </div>

        {target && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4">
              <h2 className="text-lg font-bold text-dark-invirtus">
                Confirm cleanup
              </h2>
              <div className="text-sm text-dark-invirtus/70 space-y-2">
                <p>
                  This permanently deletes <strong>{target.email}</strong> and:
                </p>
                <ul className="text-xs text-dark-invirtus/60 list-disc pl-5 space-y-0.5">
                  <li>their UserPlan + usage events</li>
                  <li>all invitations addressed to this email</li>
                  <li>their tenant (if they were the sole member)</li>
                  <li>their Auth0 record (so the email can re-sign-up)</li>
                  {hasBusinessData(target) && (
                    <li className="text-amber-700">
                      with Force on: their {target.job_offers} job offer(s),
                      candidates, interviews, and {target.payment_transactions} payment(s)
                    </li>
                  )}
                </ul>
              </div>
              <div className="space-y-1">
                <Label htmlFor="confirm-email" className="text-dark-invirtus">
                  Type the email to confirm
                </Label>
                <Input
                  id="confirm-email"
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  rounded="lg"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="reason" className="text-dark-invirtus">
                  Reason (audit log)
                </Label>
                <Input
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rounded="lg"
                  placeholder="e.g. dev test cleanup"
                />
              </div>
              {hasBusinessData(target) && (
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={force}
                    onChange={(e) => setForce(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span className="text-xs text-dark-invirtus">
                    <strong className="text-amber-700">Force wipe</strong> — also delete this user's job offers,
                    candidates, interviews, and payment transactions. Required when business data exists.
                  </span>
                </label>
              )}
              <div className="flex gap-3 pt-2">
                <Button
                  label="Cancel"
                  type="outlined"
                  intent="muted"
                  className="flex-1"
                  onClick={() => setTarget(null)}
                />
                <Button
                  label="Reset user"
                  intent="danger"
                  className="flex-1"
                  onClick={handleCleanup}
                  disabled={
                    submitting ||
                    confirmEmail !== target.email ||
                    !reason.trim() ||
                    (hasBusinessData(target) && !force)
                  }
                  loading={submitting}
                />
              </div>
            </div>
          </div>
      )}
    </div>
  );
};

/** Standalone-page version for the /admin/users/cleanup route. */
export const AdminUserCleanupPage: React.FC = () => (
  <div className="min-h-[100dvh] bg-background-gray">
    <AdminUserCleanupSection />
  </div>
);

export default AdminUserCleanupPage;
